import { Injectable, Signal, inject, DestroyRef, computed } from '@angular/core';
import { MediaMatcher } from '@angular/cdk/layout';
import { signal } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class MediaMatcherService {
  private mediaMatcher = inject(MediaMatcher);
  private mediaQueryRegistry: Map<string, MediaQueryRegistryEntry> = new Map();

  public create(query: string, destroyRef: DestroyRef): Signal<boolean> {
    destroyRef.onDestroy(() => {
      const { mediaQueryList, destroyRefs } = this.mediaQueryRegistry.get(query) || {};
      if (!mediaQueryList || !destroyRefs) return;

      destroyRefs.delete(destroyRef);
      if (!destroyRefs.size) {
        mediaQueryList.onchange = null;
        this.mediaQueryRegistry.delete(query);
      }
    });

    const existingEntry = this.mediaQueryRegistry.get(query);
    if (existingEntry) {
      existingEntry.destroyRefs.add(destroyRef);
      return existingEntry.matchesComputed;
    }

    const mediaQueryList = this.mediaMatcher.matchMedia(query);
    const matchesSignal = signal(mediaQueryList.matches);
    mediaQueryList.onchange = ({ matches }: MediaQueryListEvent) => matchesSignal.set(matches);

    const matchesComputed = computed(() => matchesSignal());
    this.mediaQueryRegistry.set(query, {
      mediaQueryList,
      matchesComputed,
      destroyRefs: new Set([destroyRef])
    });

    return matchesComputed;
  }
}

type MediaQueryRegistryEntry = {
  mediaQueryList: MediaQueryList;
  matchesComputed: Signal<boolean>;
  destroyRefs: Set<DestroyRef>;
};
